import { observable, action, computed } from 'mobx';
import Alert from 'react-s-alert';

import { App, ArrayApp, Input, ModelRegistry, AppRegistry } from 'core/index';

import { PermissionedModel } from 'apps/permissions/app';
import { FormStore } from 'libs/forms/app';
import { clientStore } from 'apps/client/app';
import { modalStore } from 'apps/modals/app';
import { ProfileModel, ProfileApp } from 'apps/profile/app';
import { raiseUserError } from 'apps/utils';

import { conf } from 'apps/comments/config.yaml';

import i18next from 'i18next';

export class CommentModel extends PermissionedModel {
  static registryName = 'CommentModel';
  static ModelProps = {
    creatorProfile: ProfileModel,
    content: 'ContentModel',
  };

  @observable id = '';
  @observable body = '';
  @observable creatorProfile = null;
  @observable originId = 0;
  @observable replytoId = 0;
  @observable dateCreated = '';
  @observable lastUpdated = '';
  @observable content = null;
  @observable inSpace = null;
  @observable origin = null;
  @observable snippet = null;
}

ModelRegistry.add(CommentModel);

export class CommentApp extends App {
  static registryName = 'CommentApp';
  static AppProps = {
    creatorProfile: ProfileApp,
    content: 'ContentApp',
  };

  static Model = CommentModel;
  @observable editing = false;

  @computed get route() {
    if (!this.originApp) return {};
    return this.originApp.route;
  }

  @computed get originApp() {
    if (!(this.origin && this.origin.__typename)) return null;
    const AppClass = AppRegistry.get(`${this.origin.__typename}App`);
    return AppClass ? new AppClass(this, this.origin) : null;
  }

  @action Edit = () => {
    this.editing = true;
    this.form = new FormStore(this, conf, 'commentForm', '', false);
    this.commentFormInput = new Input({
      id: () => this.id,
      origin: () => this.id,
      body: () => this.body,
    });
  };

  @action Cancel = () => {
    this.ResetLocals();
    this.editing = false;
    this.form = null;
  };

  @action Submit = () => {
    if (this.form.isValid) {
      this.form.SetPosting(true);
      clientStore.SetPosting(true);
      this.manager.Submit({
        onSysErr: this.onSubmitErr,
        onUserErr: this.onSubmitErr,
        onSuccess: this.onSubmitSuccess,
      });
    } else {
      this.form.alert();
    }
  };

  @action onSubmitErr = error => {
    this.form.SetPosting(false);
    clientStore.SetPosting(false);
    raiseUserError(error, 'save', 'Comment');
  };

  @action onSubmitSuccess = comment => {
    const { commentsConnection } = this.manager;
    commentsConnection.loadMore();

    clientStore.SetPosting(false);

    this.form.SetPosting(false);
    this.form.clearDraft();
    this.form.fieldStores.get('body').ResetValue();
  };

  @action Update = () => {
    if (this.form.isValid) {
      this.form.SetPosting(true);
      clientStore.SetPosting(true);
      this.manager.UpdateComment(this.commentFormInput, {
        onSysErr: this.onSaveSysErr,
        onUserErr: this.onSaveUserErr,
        onSuccess: this.onSaveSuccess,
      });
    } else {
      this.form.alert();
    }
  };

  @action onSaveSysErr = () => {
    this.ResetLocals();
    Alert.error(
      i18next.t('Hmm... We failed to update your Comment. Please try again.'),
    );
  };

  @action onSaveUserErr = error => {
    this.ResetLocals();
    raiseUserError(error, 'save', 'Comment');
  };

  @action onSaveSuccess = () => {
    this.form.SetPosting(false);
    this.form.clearDraft();
    clientStore.SetPosting(false);
    this.editing = false;
  };

  @action Delete = () => {
    this.deleted = true;
    const input = new Input({
      id: this.id,
      deleted: this.deleted,
    });
    this.manager.UpdateComment(input, {
      onSysErr: this.onDeleteErr,
      onUserErr: this.onDeleteErr,
      onSuccess: this.onDeleteSuccess,
    });
  };

  @action onDeleteErr = error => {
    this.ResetLocals();
    raiseUserError(error, 'delete', 'Comment');
  };

  @action onDeleteSuccess = () => {
    this.ResetLocals();
    modalStore.ClearComponent();
    this.manager.deletedComments += 1;
    if (this.manager.arrayApp) {
      this.manager.arrayApp.remove(this);
    }
    Alert.success('Comment deleted succesfully.');
  };
}
AppRegistry.add(CommentApp);

export class CommentArrayApp extends ArrayApp {
  static registryName = 'CommentArrayApp';
  static ElementApp = CommentApp;
}

AppRegistry.add(CommentArrayApp);
